/**
 * Referral Codes Store Slice
 *
 * Zustand slice for referral code management.
 * Handles listing, creating, viewing, and revoking referral codes.
 *
 * @see GW-1842 Referral Codes management page
 * @see GW-1584 Referral Code Revenue Sharing epic
 */

import {
  clearError as clearOpError,
  hasOperationLoaded,
  isOperationLoading,
  setError,
  setLoaded,
  setLoading,
} from '@gw/ui';

import { referralCodesApi } from '@/api/referralCodes';
import { createAsyncSliceAction } from '@/stores/helpers/asyncSliceAction';
import type {
  ReferralCode,
  ReferralCodeStatus,
  CreateReferralCodeRequest,
  ListReferralCodesResponse,
} from '@/types/referralCode.types';

import type { ErrorStates, ErrorStatuses, LoadedTimestamps, LoadingStates } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface ReferralCodesSlice {
  // State properties
  referralCodes: ReferralCode[];
  currentReferralCode: ReferralCode | null;
  referralCodesPagination: {
    nextToken?: string;
    hasMore: boolean;
  };
  referralCodesStatusFilter: ReferralCodeStatus | '';
  referralCodesIsLoading: LoadingStates;
  referralCodesHasLoaded: LoadingStates;
  referralCodesLastLoaded: LoadedTimestamps;
  referralCodesErrors: ErrorStates;
  referralCodesErrorStatuses: ErrorStatuses;

  // Referral code listing actions
  fetchReferralCodes: (orgId: string, status?: ReferralCodeStatus | '') => Promise<void>;
  fetchReferralCode: (orgId: string, referralCodeId: string) => Promise<void>;
  loadMoreReferralCodes: (orgId: string) => Promise<void>;

  // Referral code management actions
  createReferralCode: (orgId: string, request: CreateReferralCodeRequest) => Promise<ReferralCode>;
  revokeReferralCode: (orgId: string, referralCodeId: string) => Promise<void>;

  // Filter actions
  setReferralCodesStatusFilter: (status: ReferralCodeStatus | '') => void;

  // Utility actions
  clearReferralCodesError: (operation?: string) => void;
  clearCurrentReferralCode: () => void;
  resetReferralCodes: () => void;
  checkReferralCodesIsLoading: (operation: string) => boolean;
  checkReferralCodesHasLoaded: (operation: string) => boolean;
}

// ============================================================================
// Initial State
// ============================================================================

const initialReferralCodesState = {
  referralCodes: [] as ReferralCode[],
  currentReferralCode: null as ReferralCode | null,
  referralCodesPagination: {
    nextToken: undefined as string | undefined,
    hasMore: false,
  },
  referralCodesStatusFilter: '' as ReferralCodeStatus | '',
  referralCodesIsLoading: {} as LoadingStates,
  referralCodesHasLoaded: {} as LoadingStates,
  referralCodesLastLoaded: {} as LoadedTimestamps,
  referralCodesErrors: {} as ErrorStates,
  referralCodesErrorStatuses: {} as ErrorStatuses,
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createReferralCodesSlice: StateCreator<ReferralCodesSlice> = (set, get) => ({
  ...initialReferralCodesState,

  // ============================================================================
  // Referral Code Listing Actions
  // ============================================================================

  /**
   * Fetch referral codes for an organization, optionally filtered by status
   */
  fetchReferralCodes: async (orgId: string, status?: ReferralCodeStatus | '') => {
    const operation = 'fetchReferralCodes';
    if (isOperationLoading(get().referralCodesIsLoading, operation)) return;

    const filterStatus = status !== undefined ? status : get().referralCodesStatusFilter;

    await createAsyncSliceAction<ReferralCodesSlice, ListReferralCodesResponse>(
      operation,
      () =>
        referralCodesApi.listReferralCodes(orgId, {
          status: filterStatus || undefined,
        }),
      (response) => ({
        referralCodes: response.referralCodes,
        referralCodesPagination: {
          nextToken: response.nextToken,
          hasMore: !!response.nextToken,
        },
      }),
      set,
      get,
      { prefix: 'referralCodes' }
    )();
  },

  /**
   * Fetch a single referral code by ID
   */
  fetchReferralCode: async (orgId: string, referralCodeId: string) => {
    const operation = 'fetchReferralCode';
    if (isOperationLoading(get().referralCodesIsLoading, operation)) return;

    await createAsyncSliceAction<ReferralCodesSlice, ReferralCode>(
      operation,
      () => referralCodesApi.getReferralCode(orgId, referralCodeId),
      (referralCode) => ({ currentReferralCode: referralCode }),
      set,
      get,
      { prefix: 'referralCodes' }
    )();
  },

  /**
   * Load more referral codes (pagination)
   */
  loadMoreReferralCodes: async (orgId: string) => {
    const operation = 'loadMoreReferralCodes';
    const currentState = get();

    if (!currentState.referralCodesPagination.hasMore) return;
    if (isOperationLoading(currentState.referralCodesIsLoading, operation)) return;

    set({
      referralCodesIsLoading: setLoading(currentState.referralCodesIsLoading, operation, true),
      referralCodesErrors: clearOpError(currentState.referralCodesErrors, operation),
    });

    try {
      const filterStatus = currentState.referralCodesStatusFilter;
      const response = await referralCodesApi.listReferralCodes(orgId, {
        status: filterStatus || undefined,
        nextToken: currentState.referralCodesPagination.nextToken,
      });

      const state = get();
      const loadedState = setLoaded(
        state.referralCodesHasLoaded,
        state.referralCodesLastLoaded,
        operation,
        true
      );

      set({
        referralCodes: [...state.referralCodes, ...response.referralCodes],
        referralCodesPagination: {
          nextToken: response.nextToken,
          hasMore: !!response.nextToken,
        },
        referralCodesIsLoading: setLoading(state.referralCodesIsLoading, operation, false),
        referralCodesHasLoaded: loadedState.hasLoaded,
        referralCodesLastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load more referral codes';
      const loadedState = setLoaded(
        state.referralCodesHasLoaded,
        state.referralCodesLastLoaded,
        operation,
        false
      );

      set({
        referralCodesIsLoading: setLoading(state.referralCodesIsLoading, operation, false),
        referralCodesErrors: setError(state.referralCodesErrors, operation, errorMessage),
        referralCodesHasLoaded: loadedState.hasLoaded,
        referralCodesLastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  // ============================================================================
  // Referral Code Management Actions
  // ============================================================================

  /**
   * Create a new referral code
   */
  createReferralCode: async (orgId: string, request: CreateReferralCodeRequest) => {
    const operation = 'createReferralCode';
    const state = get();

    set({
      referralCodesIsLoading: setLoading(state.referralCodesIsLoading, operation, true),
      referralCodesErrors: clearOpError(state.referralCodesErrors, operation),
    });

    try {
      const referralCode = await referralCodesApi.createReferralCode(orgId, request);

      const currentState = get();
      const loadedState = setLoaded(
        currentState.referralCodesHasLoaded,
        currentState.referralCodesLastLoaded,
        operation,
        true
      );

      // Prepend new code to list and invalidate fetch cache
      set({
        referralCodes: [referralCode, ...currentState.referralCodes],
        referralCodesIsLoading: setLoading(currentState.referralCodesIsLoading, operation, false),
        referralCodesHasLoaded: {
          ...loadedState.hasLoaded,
          fetchReferralCodes: false,
        },
        referralCodesLastLoaded: loadedState.lastLoaded,
      });

      return referralCode;
    } catch (error) {
      const currentState = get();
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create referral code';
      const loadedState = setLoaded(
        currentState.referralCodesHasLoaded,
        currentState.referralCodesLastLoaded,
        operation,
        false
      );

      set({
        referralCodesIsLoading: setLoading(currentState.referralCodesIsLoading, operation, false),
        referralCodesErrors: setError(currentState.referralCodesErrors, operation, errorMessage),
        referralCodesHasLoaded: loadedState.hasLoaded,
        referralCodesLastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  /**
   * Revoke a referral code
   */
  revokeReferralCode: async (orgId: string, referralCodeId: string) => {
    const operation = 'revokeReferralCode';
    const state = get();

    set({
      referralCodesIsLoading: setLoading(state.referralCodesIsLoading, operation, true),
      referralCodesErrors: clearOpError(state.referralCodesErrors, operation),
    });

    try {
      await referralCodesApi.revokeReferralCode(orgId, referralCodeId);

      const currentState = get();
      const loadedState = setLoaded(
        currentState.referralCodesHasLoaded,
        currentState.referralCodesLastLoaded,
        operation,
        true
      );

      // Update the status in the list
      set({
        referralCodes: currentState.referralCodes.map((rc) =>
          rc.referralCodeId === referralCodeId ? { ...rc, status: 'revoked' as const } : rc
        ),
        currentReferralCode:
          currentState.currentReferralCode?.referralCodeId === referralCodeId
            ? { ...currentState.currentReferralCode, status: 'revoked' as const }
            : currentState.currentReferralCode,
        referralCodesIsLoading: setLoading(currentState.referralCodesIsLoading, operation, false),
        referralCodesHasLoaded: loadedState.hasLoaded,
        referralCodesLastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const currentState = get();
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to revoke referral code';
      const loadedState = setLoaded(
        currentState.referralCodesHasLoaded,
        currentState.referralCodesLastLoaded,
        operation,
        false
      );

      set({
        referralCodesIsLoading: setLoading(currentState.referralCodesIsLoading, operation, false),
        referralCodesErrors: setError(currentState.referralCodesErrors, operation, errorMessage),
        referralCodesHasLoaded: loadedState.hasLoaded,
        referralCodesLastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  // ============================================================================
  // Filter Actions
  // ============================================================================

  setReferralCodesStatusFilter: (status: ReferralCodeStatus | '') => {
    set({ referralCodesStatusFilter: status });
  },

  // ============================================================================
  // Utility Actions
  // ============================================================================

  clearReferralCodesError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({ referralCodesErrors: clearOpError(currentState.referralCodesErrors, operation) });
    } else {
      set({ referralCodesErrors: {} });
    }
  },

  clearCurrentReferralCode: () => {
    set({ currentReferralCode: null });
  },

  resetReferralCodes: () => {
    set(initialReferralCodesState);
  },

  checkReferralCodesIsLoading: (operation: string) =>
    isOperationLoading(get().referralCodesIsLoading, operation),

  checkReferralCodesHasLoaded: (operation: string) =>
    hasOperationLoaded(get().referralCodesHasLoaded, operation),
});
